import React, { useEffect, useState } from 'react';
import { useSettings } from '../contexts/SettingsContext';

/**
 * Toast - Professional notification component
 * Displays messages at bottom-right corner with auto-dismiss
 * Uses centralized theme colors from SettingsContext
 *
 * Props:
 *   message (string) - The message to display
 *   type ('success' | 'error' | 'warning' | 'info') - Toast variant
 *   onClose (function) - Called when toast is dismissed
 *   duration (number) - Auto-dismiss duration in ms (default: 4000)
 *   visible (boolean) - Whether the toast is visible
 */
const Toast = ({ message, type = 'info', onClose, duration = 4000, visible = true }) => {
  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';

  const [isShowing, setIsShowing] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (visible && message) {
      // Small delay for enter animation
      const enterTimeout = setTimeout(() => setIsShowing(true), 50);

      // Auto dismiss
      const dismissTimeout = setTimeout(() => {
        handleClose();
      }, duration);

      return () => {
        clearTimeout(enterTimeout);
        clearTimeout(dismissTimeout);
      };
    }
  }, [visible, message, duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      setIsShowing(false);
      setIsLeaving(false);
      if (onClose) onClose();
    }, 300);
  };

  if (!visible || !message) return null;

  // Type-specific styles
  const typeConfig = {
    success: {
      bg: 'bg-white dark:bg-gray-900',
      border: secondaryColor,
      iconBg: `${secondaryColor}15`,
      iconColor: secondaryColor,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    error: {
      bg: 'bg-white dark:bg-gray-900',
      border: '#EF4444',
      iconBg: '#EF444415',
      iconColor: '#EF4444',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    warning: {
      bg: 'bg-white dark:bg-gray-900',
      border: '#F59E0B',
      iconBg: '#F59E0B15',
      iconColor: '#F59E0B',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    },
    info: {
      bg: 'bg-white dark:bg-gray-900',
      border: primaryColor,
      iconBg: `${primaryColor}15`,
      iconColor: primaryColor,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  };

  const config = typeConfig[type] || typeConfig.info;

  return (
    <div
      className={`fixed bottom-6 right-6 z-[99999] max-w-sm w-full pointer-events-auto transition-all duration-300 ease-out ${
        isShowing && !isLeaving
          ? 'translate-x-0 opacity-100'
          : 'translate-x-full opacity-0'
      }`}
    >
      <div
        className={`${config.bg} rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden`}
        style={{ borderLeftWidth: '4px', borderLeftColor: config.border }}
      >
        <div className="flex items-start gap-3 px-4 py-3.5">
          {/* Icon */}
          <div
            className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5"
            style={{ backgroundColor: config.iconBg, color: config.iconColor }}
          >
            {config.icon}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
              {type}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 leading-snug">
              {message}
            </p>
          </div>

          {/* Close */}
          <button
            onClick={handleClose}
            className="flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress bar for auto-dismiss */}
        <div className="h-1 w-full bg-gray-100 dark:bg-gray-800">
          <div
            className="h-full transition-all ease-linear"
            style={{
              backgroundColor: config.border,
              animation: isShowing && !isLeaving ? `toast-progress ${duration}ms linear forwards` : 'none',
            }}
          />
        </div>

        <style>{`
          @keyframes toast-progress {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default Toast;
